import { useMemo } from "react";
import { HospitalityListing, TRACK_DOCUMENT_TYPES } from "../../../../types";
import EditFieldModal from "@/modules/common/components/modals/edit-field-modal";
import z from "zod";
import {
  HospitalityListingSchema,
  getHourOption,
} from "@/modules/property-tracks/utils/listing";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { showToast } from "@/modules/common/utils";
import { TOAST_IDS, ToastType } from "@/modules/common/types";
import HospitalityListingForm from "./form";
import { useEditHospitalityListing } from "@/modules/property-tracks/hooks/listings/edit-hospitality-listing.hook";
import { useGetRoomTypes } from "@/modules/property-tracks/hooks/listings/get-room-types.hook";
import { useTrackDocumentQuery } from "@/modules/property-tracks/hooks/track-document.hook";
import ImageSelector from "@/modules/common/components/input/image-selector";
import { Box, FormHelperText, Typography } from "@mui/material";
import { CurrencyCode } from "@/modules/property-tracks/dtos";
import { ViewTypes } from "@/modules/property-tracks/types";

type HospitalityListingFormFields = z.infer<
  ReturnType<typeof HospitalityListingSchema>
>;

export default function EditHospitalityListingModal({
  listing,
  trackUid,
  open,
  handleOpen,
  handleClose,
}: {
  listing: HospitalityListing;
  trackUid?: string;
  open: boolean;
  handleOpen: () => void;
  handleClose: () => void;
}) {
  const { data: roomTypes } = useGetRoomTypes();
  const roomTypeOptions = (roomTypes ?? []).map((rt) => ({
    label: rt.name,
    value: String(rt.id),
  }));

  const { data: images, isLoading: imagesLoading } = useTrackDocumentQuery({
    uid: trackUid,
    documentType: TRACK_DOCUMENT_TYPES.Media,
  });

  const defaultValues: HospitalityListingFormFields = useMemo(
    () => ({
      name: listing.name,
      nameAr: listing.nameAr ?? undefined,
      description: listing.description ?? undefined,
      descriptionAr: listing.descriptionAr ?? undefined,
      price: listing.price,
      priceForExtraPerson: listing.priceForExtraPerson ?? undefined,
      currencyCode: listing.currencyCode
        ? {
            label:
              CurrencyCode[listing.currencyCode as keyof typeof CurrencyCode] ??
              listing.currencyCode,
            value: listing.currencyCode,
          }
        : { label: CurrencyCode.EGP, value: "EGP" },
      checkInTimeStart: getHourOption(listing.checkInTimeStart),
      checkInTimeEnd: getHourOption(listing.checkInTimeEnd),
      checkOutTime: getHourOption(listing.checkOutTime),
      roomType: listing.roomType
        ? { label: listing.roomType.name, value: String(listing.roomType.id) }
        : undefined,
      minNights: listing.minNights ?? undefined,
      maxNights: listing.maxNights ?? undefined,
      airbnbOfficialListingMarkup:
        listing.airbnbOfficialListingMarkup ?? undefined,
      bookingEngineMarkup: listing.bookingEngineMarkup ?? undefined,
      isActive: listing.isActive,
      isFeatured: listing.isFeatured ?? false,
      maxNumberOfGuests: listing.maxNumberOfGuests ?? undefined,
      bedCount: listing.bedCount ?? undefined,
      singleBedCount: listing.singleBedCount ?? undefined,
      kingBedCount: listing.kingBedCount ?? undefined,
      twinBedCount: listing.twinBedCount ?? undefined,
      queenBedCount: listing.queenBedCount ?? undefined,
      sofaBedCount: listing.sofaBedCount ?? undefined,
      viewType: listing.viewType
        ? {
            label:
              ViewTypes[listing.viewType as keyof typeof ViewTypes] ??
              listing.viewType,
            value: listing.viewType,
          }
        : undefined,
      isExtraBedAllowed: listing.isExtraBedAllowed ?? false,
      isSmokingAllowed: listing.isSmokingAllowed ?? false,
      isPetsAllowed: listing.isPetsAllowed ?? false,
      isPartiesAllowed: listing.isPartiesAllowed ?? false,
      instantBookable: listing.instantBookable,
      documentUids: listing.hospitalityListingMedia?.map(
        (m) => m.trackDocument.uid,
      ),
    }),
    [listing],
  );

  const { handleSubmit, reset, control } =
    useForm<HospitalityListingFormFields>({
      resolver: zodResolver(HospitalityListingSchema()),
      values: defaultValues,
    });

  const mutation = useEditHospitalityListing();

  const onSubmit: SubmitHandler<HospitalityListingFormFields> = (data) => {
    mutation.mutate(
      {
        uid: listing.uid,
        data: {
          name: data.name,
          nameAr: data.nameAr || undefined,
          description: data.description || undefined,
          descriptionAr: data.descriptionAr || undefined,
          price: data.price,
          priceForExtraPerson: data.priceForExtraPerson || undefined,
          currencyCode: data.currencyCode.value as any,
          checkInTimeStart: data.checkInTimeStart?.value
            ? Number(data.checkInTimeStart.value)
            : undefined,
          checkInTimeEnd: data.checkInTimeEnd?.value
            ? Number(data.checkInTimeEnd.value)
            : undefined,
          checkOutTime: data.checkOutTime?.value
            ? Number(data.checkOutTime.value)
            : undefined,
          roomTypeId: data.roomType?.value
            ? Number(data.roomType.value)
            : undefined,
          minNights: data.minNights ?? undefined,
          maxNights: data.maxNights ?? undefined,
          airbnbOfficialListingMarkup:
            data.airbnbOfficialListingMarkup ?? undefined,
          bookingEngineMarkup: data.bookingEngineMarkup ?? undefined,
          isActive: data.isActive,
          isFeatured: data.isFeatured ?? undefined,
          documentUids: data.documentUids ?? undefined,
          maxNumberOfGuests: data.maxNumberOfGuests ?? undefined,
          bedCount: data.bedCount ?? undefined,
          singleBedCount: data.singleBedCount ?? undefined,
          kingBedCount: data.kingBedCount ?? undefined,
          twinBedCount: data.twinBedCount ?? undefined,
          queenBedCount: data.queenBedCount ?? undefined,
          sofaBedCount: data.sofaBedCount ?? undefined,
          viewType: (data.viewType?.value as any) ?? undefined,
          isExtraBedAllowed: data.isExtraBedAllowed ?? false,
          isSmokingAllowed: data.isSmokingAllowed ?? false,
          isPetsAllowed: data.isPetsAllowed ?? false,
          isPartiesAllowed: data.isPartiesAllowed ?? false,
          instantBookable: data.instantBookable,
        },
      },
      {
        onSuccess: () => {
          handleModalClose();
          showToast({
            type: ToastType.SUCCESS,
            message: "Hospitality listing updated successfully",
            toastId: TOAST_IDS.EDIT_LISTING_SUCCESS,
            options: { position: "bottom-left", autoClose: 3000 },
          });
        },
        onError: (error) => {
          showToast({
            type: ToastType.ERROR,
            message:
              error?.response?.data?.message ||
              "Unexpected error occurred. Please try again.",
            toastId: TOAST_IDS.EDIT_LISTING_ERROR,
            options: { position: "bottom-left", autoClose: 3000 },
          });
        },
      },
    );
  };

  const handleModalClose = () => {
    handleClose();
    reset();
  };

  const handleModalOpen = () => {
    handleOpen();
    reset(defaultValues);
  };

  return (
    <EditFieldModal
      open={open}
      setOpen={handleModalOpen}
      header="Edit Hospitality Listing"
      width={580}
      handleConfirm={handleSubmit(onSubmit)}
      handleCloseModal={handleModalClose}
      isLoading={mutation.isPending}
    >
      <HospitalityListingForm
        control={control}
        roomTypeOptions={roomTypeOptions}
      />
      <Controller
        control={control}
        name="documentUids"
        render={({ field, fieldState }) => (
          <Box>
            <Typography
              sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
            >
              Listing Media
            </Typography>
            <Typography
              sx={(t) => t.nawy.typography.english.variants.body.xs.regular}
            >
              Select photos
            </Typography>
            <ImageSelector
              images={images ?? []}
              loading={imagesLoading}
              selectedImagesIds={field.value ?? []}
              setSelectedImagesIds={(ids: string[]) => field.onChange(ids)}
            />
            {fieldState.error && (
              <FormHelperText error>{fieldState.error.message}</FormHelperText>
            )}
          </Box>
        )}
      />
    </EditFieldModal>
  );
}
